import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { twMerge } from "@/lib/twMerge";
import { UserBadge } from "@/auth/UserBadge";

/** One top-bar tab. `end` keeps Home from showing as active on every route (its path, "/",
 * prefixes them all); `also` lists other paths the tab owns (a practice's page lives on the
 * Standards tab). */
interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  also?: string[];
}

/** The tabs in their three groups: Home on its own; the catalog — what Guild's platform has
 * and expects (capabilities, standards and practices, tools); and changes, which move through
 * their pages in order (a change is mapped, reviewed, then becomes a delivery brief), so that
 * group is drawn as a flow. */
const GROUPS: { label: string | null; flow?: boolean; items: NavItem[] }[] = [
  { label: null, items: [{ to: "/", label: "Home", end: true }] },
  {
    label: "Catalog",
    items: [
      { to: "/registry", label: "Capabilities" },
      { to: "/standards", label: "Standards", also: ["/practices"] },
      { to: "/tools", label: "Tools" },
    ],
  },
  {
    label: "Changes",
    flow: true,
    items: [
      { to: "/map", label: "Map a change" },
      { to: "/review", label: "Review" },
      { to: "/briefs", label: "Delivery briefs" },
    ],
  },
];

/** The frame around every page: the sticky top bar (logo, page tabs, signed-in user) and
 * the centered content column the current route renders into via `<Outlet />`. */
export function AppShell() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-bg">
      {/* Sticky top bar with segmented-control tab groups (a pill sliding inside a sunken
          track), matching the original registry-editor.html rather than an underline-tab
          pattern — one track per group of pages, each labelled. Stays 60px tall: the browse
          pages' split layout is sized to it. See frontend/README.md's design-parity note. */}
      <header className="sticky top-0 z-20 h-[60px] border-b border-line bg-surface shadow-[inset_0_3px_0_var(--color-brand)]">
        <div className="mx-auto flex h-full max-w-6xl items-center gap-6 px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <div className="grid h-7 w-7 place-items-center rounded bg-g-orange font-[family-name:--font-display] text-g-off-black">
              G
            </div>
            <h1 className="whitespace-nowrap font-[family-name:--font-display] text-base font-medium tracking-tight">
              Capability Registry
            </h1>
          </Link>
          <nav aria-label="Main" className="flex shrink-0 items-end gap-4 whitespace-nowrap text-[13px]">
            {GROUPS.map((group) => (
              <div
                key={group.label ?? "home"}
                role={group.label ? "group" : undefined}
                aria-label={group.label ?? undefined}
                className="flex flex-col gap-0.5"
              >
                {/* Home has no label; an empty one keeps its track level with the others. */}
                <span
                  aria-hidden="true"
                  className="h-3 px-1 text-[10px] font-semibold leading-3 tracking-wide text-text/50 uppercase"
                >
                  {group.label}
                </span>
                <div className="flex items-center gap-0.5 rounded bg-sunken p-[3px]">
                  {group.items.map((item, i) => (
                    <span key={item.to} className="flex items-center gap-0.5">
                      {group.flow && i > 0 && (
                        <span aria-hidden="true" className="px-0.5 text-text/40">
                          ›
                        </span>
                      )}
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          twMerge(
                            "rounded-[5px] px-3 py-[5px] text-text/70 hover:text-text",
                            (isActive || item.also?.some((p) => pathname.startsWith(p))) &&
                              "bg-surface font-medium text-text",
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="ml-auto flex min-w-0 items-center gap-3 whitespace-nowrap text-sm text-text/60">
            <UserBadge />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-8 py-7">
        <Outlet />
      </main>
    </div>
  );
}
